import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  query,
  addDoc,
  orderBy,
  where,
  updateDoc,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  getDocs,
  onSnapshot,
  Unsubscribe,
  limit,
  startAfter,
  QueryConstraint,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { useCollectionData } from "react-firebase-hooks/firestore";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween.js";

dayjs.extend(isBetween);

import firebaseService from "@/firebaseConfig";

import { storage } from "@/utils/Storage";

import { IRoleType } from "@/models/role";
import {
  Channel,
  CHAT_LIST_TYPE,
  CONVERSATION_STATUS,
  CONVERSATION_TYPE,
  IChatSubCollection,
  IGlobalUnreadCounts,
  ITabIndex,
  Message,
  UseFirebaseMessagingProps,
  UseFirebaseMessagingReturn,
} from "@/models/chat";
import { ISessionKeys } from "@/models";

import { PAGE_SIZE } from "../Config";

import { useAppSelector } from "@/store/hooks";
import { chat } from "@/store/slices/chat";

import { generateDeviceId } from "..";

const FIREBASE_COLLECTION = "conversations";

const messageConverter: FirestoreDataConverter<Message> = {
  toFirestore(message: Message) {
    const { uniqueId, ...data } = message;
    return data;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): Message {
    const data = snapshot.data(options);
    return {
      ...data,
      uniqueId: snapshot.id,
    } as Message;
  },
};

const channelConverter: FirestoreDataConverter<Channel> = {
  toFirestore(channel: Channel) {
    const { uniqueId, ...data } = channel;
    return data;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): Channel {
    const data = snapshot.data(options);
    return {
      ...data,
      uniqueId: snapshot.id,
    } as Channel;
  },
};

const useFirebaseMessaging = ({
  chatListType = CHAT_LIST_TYPE.INTERNAL,
}: UseFirebaseMessagingProps): UseFirebaseMessagingReturn => {
  const [listType, setListType] = useState<CHAT_LIST_TYPE>(chatListType);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [enrichedChannelList, setEnrichedChannelList] = useState<Channel[]>([]);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [paginatedDocs, setPaginatedDocs] = useState<Channel[]>([]);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMoreDocs, setHasMoreDocs] = useState<boolean>(true);
  const [switchChatTab, setSwitchChatTab] = useState<ITabIndex>(
    ITabIndex.APPOINTMENTS
  );
  const [globalUnreadCounts, setGlobalUnreadCounts] =
    useState<IGlobalUnreadCounts>({
      appointment: { mentionCount: 0, unreadCount: 0 },
      inPatient: { mentionCount: 0, unreadCount: 0 },
    });
  const [supportChat, setSupportChat] = useState<Channel | null>(null);
  const isInitialListenerAttach = useRef(true);

  const { inPatientFilter, selectedChat } = useAppSelector(chat);

  const sessionKeys = [ISessionKeys.USER_ID, ISessionKeys.ROLE_TYPE];
  const [currentUserId, roleType] = sessionKeys.map((key) =>
    storage.getSessionToken(key)
  );

  const doctorOrJrDoctor =
    roleType === IRoleType.DOCTOR || roleType === IRoleType.JR_DOCTOR;

  const conversationLog = collection(firebaseService.db, FIREBASE_COLLECTION);

  const patientBaseQuery = useMemo(() => {
    const conversationRef = collection(firebaseService.db, FIREBASE_COLLECTION);
    return query(
      conversationRef.withConverter(channelConverter),
      where("users.family_members", "array-contains", currentUserId ?? ""),
      where("type", "in", [
        CONVERSATION_TYPE.APPOINTMENT,
        CONVERSATION_TYPE.IN_PATIENT,
      ]),
      orderBy("updatedAt", "desc")
    );
  }, [listType, currentUserId]);

  const fetchConversationById = async (id: string) => {
    const conversationRef = collection(
      firebaseService.db,
      FIREBASE_COLLECTION
    ).withConverter(channelConverter);

    const q = query(conversationRef, where("id", "==", id));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      console.warn("No matching document found with field id:", id);
      return null;
    }
  };

  const videoChatQuery = useMemo(() => {
    const conversationRef = collection(firebaseService.db, FIREBASE_COLLECTION);
    return query(
      conversationRef.withConverter(channelConverter),
      ...(doctorOrJrDoctor
        ? [where("users.staff_members", "array-contains", currentUserId ?? "")]
        : [
            where(
              "users.family_members",
              "array-contains",
              currentUserId ?? ""
            ),
          ]),
      where("id", "==", documentId)
    );
  }, [listType, currentUserId, documentId]);

  const supportChatRef = useMemo(() => {
    const conversationRef = collection(firebaseService.db, FIREBASE_COLLECTION);
    return query(
      conversationRef.withConverter(channelConverter),
      where("users.patient_id", "==", currentUserId ?? ""),
      where("type", "==", CONVERSATION_TYPE.SUPPORT),
      limit(1)
    );
  }, [currentUserId]);

  const chatRefs = useMemo(
    () => ({
      internal: channelId
        ? collection(
            firebaseService.db,
            FIREBASE_COLLECTION,
            channelId,
            IChatSubCollection.INTERNAL
          )
        : null,
      patient: channelId
        ? collection(
            firebaseService.db,
            FIREBASE_COLLECTION,
            channelId,
            IChatSubCollection.PATIENT
          )
        : null,
    }),
    [channelId]
  );

  const chatQueries = useMemo(
    () => ({
      internal: chatRefs.internal
        ? query(
            chatRefs.internal.withConverter(messageConverter),
            orderBy("timestamp", "asc")
          )
        : null,
      patient: chatRefs.patient
        ? query(
            chatRefs.patient.withConverter(messageConverter),
            orderBy("timestamp", "asc")
          )
        : null,
      patientMedia: chatRefs.patient
        ? query(
            chatRefs.patient.withConverter(messageConverter),
            orderBy("timestamp", "desc")
          )
        : null,
    }),
    [chatRefs]
  );

  // Use collection data hooks with proper typing
  const [channelList] = useCollectionData<Channel>(patientBaseQuery);
  const [supportChatList] = useCollectionData<Channel>(supportChatRef);
  const [videoCallChat] = useCollectionData<Channel>(videoChatQuery);

  const [internalChatList, internalLoading] = useCollectionData<Message>(
    chatQueries.internal
  );
  const [patientChatList, patientLoading] = useCollectionData<Message>(
    chatQueries.patient
  );
  const [patientChatMediaListing] = useCollectionData<Message>(
    chatQueries.patientMedia
  );

  const isLoadingMessages =
    listType === CHAT_LIST_TYPE.INTERNAL ? internalLoading : patientLoading;

  // Memoize filtered media list
  const patientChatMediaList = useMemo(
    () => patientChatMediaListing?.filter((item) => item.type === 2) ?? [],
    [patientChatMediaListing]
  );

  // Memoize messages based on list type
  const messages = useMemo(
    () =>
      listType === CHAT_LIST_TYPE.INTERNAL
        ? internalChatList ?? []
        : patientChatList ?? [],
    [internalChatList, patientChatList, listType]
  );

  // Memoized handlers
  const handleList = useCallback((list: CHAT_LIST_TYPE) => {
    setListType(list);
  }, []);

  const handleDocumentId = useCallback((id: string) => {
    setDocumentId(id);
  }, []);

  const handleChannel = useCallback((id: string) => {
    addDoc(conversationLog, {
      platform: "web",
      type: "snapshot_opened",
      timestamp: serverTimestamp(),
      deviceId: generateDeviceId(),
      channelId: id,
    }).catch((error) => {
      console.error("Error logging 'snapshot_opened' event:", error);
    });

    setChannelId(id);
  }, []);

  const handleSendMessageFromSupport = async (
    message: Record<string, any> = {}
  ) => {
    if (!channelId?.trim()) return;

    try {
      let mentions = [];
      const documentRef = doc(
        firebaseService.db,
        FIREBASE_COLLECTION,
        channelId
      );
      const docSnap = await getDoc(documentRef);
      const newLastMessage = {
        message: message?.message,
        sender_id: message?.sender_id,
        timestamp: message?.timestamp,
        type: message?.type,
        mentions: message?.mentions || [],
        unReadMessageCount: 0,
      };
      const { message: msg, ...documentData } = message;

      delete documentData.message;
      delete documentData.users;
      if (docSnap.exists()) {
        const messagesRef = collection(
          firebaseService.db,
          FIREBASE_COLLECTION,
          channelId,
          listType === CHAT_LIST_TYPE.INTERNAL
            ? IChatSubCollection.INTERNAL
            : IChatSubCollection.PATIENT
        );

        await addDocumentToCollection(message.message, listType);
        const unreadQuery = query(
          messagesRef.withConverter(messageConverter),
          where("isSeen", "==", false)
        );

        const unreadSnapshot = await getDocs(unreadQuery);

        const userData = docSnap.data();

        unreadSnapshot?.docs?.forEach((doc) =>
          mentions.push(...(doc.data()?.mentions || []))
        );

        const internal = {
          ...newLastMessage,
          mentions: mentions,
          message: newLastMessage.message?.message,
          unReadMessageCount: unreadSnapshot?.docs?.length,
          sender_id: newLastMessage?.message?.sender_id,
          timestamp: newLastMessage?.message?.timestamp,
          type: newLastMessage.message?.type,
        };
        const patient = {
          ...newLastMessage,
          mentions: mentions,
          message: newLastMessage.message?.message,
          unReadMessageCount: unreadSnapshot?.docs?.length,
          sender_id: newLastMessage?.message?.sender_id,
          timestamp: newLastMessage?.message?.timestamp,
          type: newLastMessage.message?.type,
        };

        const lastMessage = {
          ...(listType === CHAT_LIST_TYPE.INTERNAL
            ? {
                internal,
              }
            : {
                patient,
              }),
        };

        setPaginatedDocs((prev: any) =>
          prev.map((doc: any) =>
            doc.uniqueId === channelId ? { ...doc, lastMessage } : doc
          )
        );
        // Update root document with new message counts and lastMessage
        await updateDoc(documentRef, {
          ...documentData,
          ...userData,
          isChatActive: true,
          updatedAt: dayjs().valueOf(),
          ...(message?.type === CONVERSATION_TYPE.SUPPORT &&
            userData?.status === CONVERSATION_STATUS.RESOLVED && {
              status: CONVERSATION_STATUS.ACTIVE,
            }),
          lastMessage,
        });
      } else {
        const internal = {
          ...newLastMessage,
          mentions: mentions,
          message: newLastMessage.message?.message,
          unReadMessageCount: 1,
          sender_id: newLastMessage?.message?.sender_id,
          timestamp: newLastMessage?.message?.timestamp,
          type: newLastMessage.message?.type,
        };
        const patient = {
          ...newLastMessage,
          message: newLastMessage.message?.message,
          unReadMessageCount: 1,
          sender_id: newLastMessage?.message?.sender_id,
          timestamp: newLastMessage?.message?.timestamp,
          type: newLastMessage.message?.type,
        };

        const lastMessage = {
          ...(listType === CHAT_LIST_TYPE.INTERNAL
            ? {
                internal,
              }
            : {
                patient,
              }),
        };

        setPaginatedDocs((prev: any) =>
          prev.map((doc: any) =>
            doc.uniqueId === channelId ? { ...doc, lastMessage } : doc
          )
        );

        await setDoc(documentRef, {
          ...documentData,
          isChatActive: true,
          users: message?.users,
          lastMessage,
          updatedAt: dayjs().valueOf(),
        });
        await addDocumentToCollection(message.message, listType);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleSendMessage = useCallback(
    async (message: Record<string, any> = {}) => {
      if (selectedChat?.type === CONVERSATION_TYPE.SUPPORT) {
        return handleSendMessageFromSupport(message);
      }
      const participantIds = [
        ...selectedChat?.users?.family_members,
        ...selectedChat?.users?.staff_members,
      ];

      const seen = {};
      participantIds.forEach((id) => {
        seen[id] = id === message?.message?.sender_id;
      });
      if (!channelId?.trim()) return;

      try {
        const documentRef = doc(
          firebaseService.db,
          FIREBASE_COLLECTION,
          channelId
        );
        const docSnap = await getDoc(documentRef);

        const { message: msg, ...documentData } = message;

        const promises = participantIds.map(async (userId) => {
          const messagesRef = collection(
            firebaseService.db,
            FIREBASE_COLLECTION,
            channelId,
            listType === CHAT_LIST_TYPE.INTERNAL
              ? IChatSubCollection.INTERNAL
              : IChatSubCollection.PATIENT
          );

          const unreadQuery = query(
            messagesRef.withConverter(messageConverter),
            where(`seen.${userId}`, "==", false)
          );

          const unreadSnapshot = await getDocs(unreadQuery);

          const mentions =
            listType === CHAT_LIST_TYPE.INTERNAL
              ? unreadSnapshot.docs.flatMap((doc) => doc.data()?.mentions || [])
              : [];

          const data = {
            unReadMessageCount:
              userId === message?.message?.sender_id
                ? 0
                : unreadSnapshot.size + 1,
            ...(listType === CHAT_LIST_TYPE.INTERNAL && { mentions }),
          };

          return [userId, data];
        });

        const resolvedPromises = await Promise.all(promises);

        const seenLastMessage = Object.fromEntries(resolvedPromises);

        const newLastMessage = {
          message: message?.message?.message,
          sender_id: message?.message?.sender_id,
          timestamp: message?.message?.timestamp,
          type: message?.message?.type,
          seen: seenLastMessage,
        };

        delete documentData.message;
        delete documentData.users;

        if (docSnap.exists()) {
          await addDocumentToCollection({ ...message.message, seen }, listType);

          const userData = docSnap.data();
          const lastMessage = {
            ...userData.lastMessage,
            ...(listType === CHAT_LIST_TYPE.INTERNAL
              ? { internal: newLastMessage }
              : { patient: newLastMessage }),
          };

          setPaginatedDocs((prev: any) =>
            prev.map((doc: any) =>
              doc.uniqueId === channelId ? { ...doc, lastMessage } : doc
            )
          );

          const updatePayload = {
            isChatActive: true,
            updatedAt: dayjs().valueOf(),
            lastMessage,
            ...(message?.type === CONVERSATION_TYPE.SUPPORT &&
              userData?.status === CONVERSATION_STATUS.RESOLVED && {
                status: CONVERSATION_STATUS.ACTIVE,
              }),
          };

          await updateDoc(documentRef, updatePayload);
        } else {
          const lastMessage = {
            ...(listType === CHAT_LIST_TYPE.INTERNAL
              ? { internal: newLastMessage }
              : { patient: newLastMessage }),
          };

          setPaginatedDocs((prev: any) =>
            prev.map((doc: any) =>
              doc.uniqueId === channelId ? { ...doc, lastMessage } : doc
            )
          );

          await setDoc(documentRef, {
            ...documentData,
            isChatActive: true,
            users: message?.users,
            lastMessage,
            updatedAt: dayjs().valueOf(),
          });
          await addDocumentToCollection({ ...message.message, seen }, listType);
        }
      } catch (error) {
        console.error("Error sending message:", error);
      }
    },
    [channelId, listType, currentUserId, selectedChat]
  );

  const addDocumentToCollection = async (
    message: Record<string, any>,
    type: CHAT_LIST_TYPE
  ) => {
    if (!channelId) return;

    try {
      const collectionRef = collection(
        firebaseService.db,
        FIREBASE_COLLECTION,
        channelId,
        type === CHAT_LIST_TYPE.INTERNAL
          ? IChatSubCollection.INTERNAL
          : IChatSubCollection.PATIENT
      );
      await addDoc(collectionRef, message);
    } catch (error) {
      console.error("Error adding document:", error);
    }
  };

  const markMessageAsRead = useCallback(
    async (
      messageId: string,
      channelID: string,
      subcollection: IChatSubCollection
    ) => {
      if (!channelID) return;

      try {
        const documentRef = doc(
          firebaseService.db,
          FIREBASE_COLLECTION,
          channelID
        );
        const documentSnap = await getDoc(documentRef);

        const messageRef = doc(
          firebaseService.db,
          FIREBASE_COLLECTION,
          channelID,
          subcollection,
          messageId
        );
        const docSnap = await getDoc(messageRef);

        if (docSnap.exists()) {
          await updateDoc(
            messageRef,
            selectedChat?.type === CONVERSATION_TYPE.SUPPORT
              ? { isSeen: true }
              : {
                  [`seen.${currentUserId}`]: true,
                }
          );
        }
        if (documentSnap.exists()) {
          if (selectedChat?.type === CONVERSATION_TYPE.SUPPORT) {
            await updateDoc(documentRef, {
              lastMessage: {
                ...documentSnap.data().lastMessage,
                ...{
                  internal: { unReadMessageCount: 0, mentions: [] },
                  patient: { unReadMessageCount: 0, mentions: [] },
                },
              },
            });
          } else {
            const lastMessage = documentSnap.data().lastMessage;

            await updateDoc(documentRef, {
              lastMessage: {
                ...(subcollection === IChatSubCollection.INTERNAL
                  ? {
                      internal: {
                        ...lastMessage?.internal,
                        seen: {
                          ...lastMessage?.internal?.seen,
                          [currentUserId]: {
                            unReadMessageCount: 0,
                            mentions: [],
                          },
                        },
                      },
                    }
                  : {
                      patient: {
                        ...lastMessage?.patient,
                        seen: {
                          ...lastMessage?.patient?.seen,
                          [currentUserId]: {
                            unReadMessageCount: 0,
                          },
                        },
                      },
                    }),
              },
            });
          }
        }
      } catch (error) {
        console.error("Error marking message as read:", error);
      }
    },
    [selectedChat, currentUserId]
  );

  const markAllMessagesAsRead = useCallback(
    async (channelID: string, subcollection: IChatSubCollection) => {
      if (!channelID || !currentUserId) return;

      try {
        // 1. Get a reference to the main conversation document to update lastMessage
        const documentRef = doc(
          firebaseService.db,
          FIREBASE_COLLECTION,
          channelID
        );
        const documentSnap = await getDoc(documentRef);

        // 2. Get a reference to the messages subcollection
        const messagesRef = collection(
          firebaseService.db,
          FIREBASE_COLLECTION,
          channelID,
          subcollection
        );

        // 3. Query for messages where the current user's status is 'false'
        const unreadQuery = query(
          messagesRef,
          ...[
            selectedChat?.type === CONVERSATION_TYPE.SUPPORT
              ? where("isSeen", "==", false)
              : where(`seen.${currentUserId}`, "==", false),
          ]
        );

        const unreadSnapshot = await getDocs(unreadQuery);

        if (unreadSnapshot.empty) {
          console.log("No unread messages for this user.");
          return; // Nothing to update
        }

        // 4. Create a batch write to update all documents efficiently
        const batch = writeBatch(firebaseService.db);
        unreadSnapshot.docs.forEach((messageDoc) => {
          batch.update(
            messageDoc.ref,
            selectedChat?.type === CONVERSATION_TYPE.SUPPORT
              ? { isSeen: true }
              : { [`seen.${currentUserId}`]: true }
          );
        });
        await batch.commit();

        // 5. Reset the unread counts on the parent document (optional but good practice)
        if (documentSnap.exists()) {
          const lastMessage = documentSnap.data().lastMessage;

          await updateDoc(documentRef, {
            lastMessage: {
              internal: {
                ...lastMessage.internal,
                seen: {
                  ...lastMessage.internal.seen,
                  [currentUserId]: {
                    unReadMessageCount: 0,
                    mentions: [],
                  },
                },
              },
            },
            patient: {
              ...lastMessage.patient,
              ...(selectedChat?.type === CONVERSATION_TYPE.SUPPORT
                ? { unReadMessageCount: 0, mentions: [] }
                : {
                    seen: {
                      ...lastMessage.patient.seen,
                      [currentUserId]: {
                        unReadMessageCount: 0,
                      },
                    },
                  }),
            },
          });
        }
      } catch (error) {
        console.error("Error marking all messages as read:", error);
      }
    },
    [currentUserId] // Add currentUserId to dependency array
  );

  const removeMemberFromChat = useCallback(
    async (familyMemberId: string) => {
      if (!channelId) return;

      try {
        const channelRef = doc(
          firebaseService.db,
          FIREBASE_COLLECTION,
          channelId
        );
        const docSnap = await getDoc(channelRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const updatedFamilyMembers = data?.users?.family_members?.filter(
            (memberId: string) => memberId !== familyMemberId
          );

          await updateDoc(channelRef, {
            users: {
              ...data.users,
              family_members: updatedFamilyMembers,
            },
          });
        }
      } catch (error) {
        console.error("Error removing member from chat:", error);
      }
    },
    [channelId]
  );

  const updateChatStatusOrFavorite = async (
    channelId: string,
    isFavorite: boolean,
    isChatEnabled: boolean
  ) => {
    if (!channelId) return;
    try {
      const documentRef = doc(
        firebaseService.db,
        FIREBASE_COLLECTION,
        channelId
      );
      const docSnap = await getDoc(documentRef);

      if (docSnap.exists()) {
        const updateData: Record<string, any> = {};
        if (typeof isFavorite === "boolean") {
          updateData.isFavorite = isFavorite;
        }
        if (typeof isChatEnabled === "boolean") {
          updateData.isChatEnabled = isChatEnabled;
        }
        await updateDoc(documentRef, updateData);
      } else {
        const newData: Record<string, any> = {};
        if (typeof isFavorite === "boolean") {
          newData.isFavorite = isFavorite;
        }
        if (typeof isChatEnabled === "boolean") {
          newData.isChatEnabled = isChatEnabled;
        }
        await setDoc(documentRef, newData);
      }
    } catch (error) {
      console.error("Error updating chat status:", error);
    }
  };

  const subscribeToChannelMessages = (): Unsubscribe[] => {
    if (!channelList?.length) return [];

    const unsubscribes: Unsubscribe[] = [];
    let updatedChannels = [...channelList];

    addDoc(conversationLog, {
      platform: "web",
      type: "snapshot_opened",
      timestamp: serverTimestamp(),
      deviceId: generateDeviceId(),
    }).catch((error) => {
      console.error("Error logging 'snapshot_opened' event:", error);
    });

    channelList.forEach((channel, index) => {
      const internalRef = collection(
        firebaseService.db,
        FIREBASE_COLLECTION,
        channel.uniqueId,
        IChatSubCollection.INTERNAL
      ).withConverter(messageConverter);

      const patientRef = collection(
        firebaseService.db,
        FIREBASE_COLLECTION,
        channel.uniqueId,
        IChatSubCollection.PATIENT
      ).withConverter(messageConverter);

      const internalQuery = query(internalRef, orderBy("timestamp", "desc"));
      const patientQuery = query(patientRef, orderBy("timestamp", "desc"));

      const unsubInternal = onSnapshot(internalQuery, (internalSnap) => {
        handleSnapshotUpdate(internalSnap, IChatSubCollection.INTERNAL);
      });

      const unsubPatient = onSnapshot(patientQuery, (patientSnap) => {
        handleSnapshotUpdate(patientSnap, IChatSubCollection.PATIENT);
      });

      function handleSnapshotUpdate(snapshot: any, type: IChatSubCollection) {
        if (!snapshot.empty) {
          const unReadMessages = snapshot.docs.filter(
            (doc: any) =>
              doc.data()?.sender_id !== currentUserId &&
              (selectedChat?.type === CONVERSATION_TYPE.SUPPORT
                ? !doc.data()?.isSeen
                : !doc.data().seen?.[currentUserId])
          );

          const mentionCount = snapshot.docs.filter(
            (doc: any) =>
              (selectedChat?.type === CONVERSATION_TYPE.SUPPORT
                ? !doc.data()?.isSeen
                : !doc.data().seen?.[currentUserId]) &&
              doc.data()?.sender_id !== currentUserId &&
              doc
                ?.data()
                ?.mentions?.some(
                  (mention: { userId: string }) =>
                    mention.userId === currentUserId
                )
          );

          const updatedChannel = {
            ...updatedChannels[index],
            lastMessage: {
              ...updatedChannels[index].lastMessage,
              [type]: {
                ...snapshot.docs[0].data(),
                unReadMessageCount: unReadMessages.length,
                mentionCount: mentionCount.length,
              },
            },
          };

          updatedChannels[index] = updatedChannel;
        } else {
          // Empty subcollection
          const updatedChannel = {
            ...updatedChannels[index],
          };

          if (updatedChannel.lastMessage?.[type]) {
            updatedChannel.lastMessage[type] = null;
          }

          updatedChannels[index] = updatedChannel;
        }

        setEnrichedChannelList(updatedChannels);
      }

      unsubscribes.push(unsubInternal);
      unsubscribes.push(unsubPatient);
    });

    return unsubscribes;
  };
  const fetchPaginatedConversations = useCallback(
    async (conversationType: CONVERSATION_TYPE, initialFetch = false) => {
      if (isLoadingMore || (!initialFetch && !hasMoreDocs)) return;

      setIsLoadingMore(true);

      if (initialFetch) {
        setPaginatedDocs([]);
        setLastDoc(null);
        setHasMoreDocs(true);
      }

      try {
        const conversationRef = collection(
          firebaseService.db,
          FIREBASE_COLLECTION
        );
        const baseConstraints: QueryConstraint[] = [
          where("users.staff_members", "array-contains", currentUserId ?? ""),
          where("type", "==", conversationType),
          where("isChatActive", "==", true),
          ...(conversationType === CONVERSATION_TYPE.IN_PATIENT
            ? [
                ...(inPatientFilter?.selectedHospitalId?.length > 0
                  ? [
                      where(
                        "hospitalId",
                        "==",
                        inPatientFilter.selectedHospitalId
                      ),
                    ]
                  : []),
                ...(inPatientFilter?.selectedRoomId?.length > 0
                  ? [where("roomId", "==", inPatientFilter.selectedRoomId)]
                  : []),
              ]
            : []),
          orderBy("updatedAt", "desc"),
          limit(PAGE_SIZE),
        ];

        addDoc(conversationLog, {
          platform: "web",
          type: "snapshot_opened",
          timestamp: serverTimestamp(),
          deviceId: generateDeviceId(),
          channelId,
        }).catch((error) => {
          console.error("Error logging 'snapshot_opened' event:", error);
        });
        if (!initialFetch && lastDoc) {
          baseConstraints.push(startAfter(lastDoc));
        }

        const q = query(
          conversationRef.withConverter(channelConverter),
          ...baseConstraints
        );

        const querySnapshot = await getDocs(q);

        const newDocsWithLastMessage = await Promise.all(
          querySnapshot.docs.map(async (doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
            };
          })
        );

        const newDocs = newDocsWithLastMessage.filter((doc: any) => {
          const date = dayjs(doc?.bookingDate);
          const isFuture = date.isAfter(dayjs());

          const isAppointment =
            conversationType === CONVERSATION_TYPE.APPOINTMENT && isFuture
              ? doc?.isChatActive
              : doc?.bookingDate &&
                dayjs(doc?.bookingDate, "YYYY-MM-DD").isBetween(
                  dayjs().subtract(7, "day"), // Start of range: 7 days ago
                  dayjs(), // End of range: Today
                  "day",
                  "[]"
                );

          const isInPatientDischarged =
            conversationType === CONVERSATION_TYPE.IN_PATIENT &&
            doc.status === CONVERSATION_STATUS.DISCHARGED &&
            (isFuture
              ? doc?.isChatActive
              : doc?.dischargedDate &&
                dayjs(doc.dischargedDate).isBetween(
                  dayjs().subtract(15, "day"), // Start of range: 15 days ago
                  dayjs(), // End of range: Today
                  "day",
                  "[]"
                ));

          const isInPatientActive =
            conversationType === CONVERSATION_TYPE.IN_PATIENT &&
            (doc.status === CONVERSATION_STATUS.ACTIVE ||
              doc.status === CONVERSATION_STATUS.DISABLED ||
              doc.status === CONVERSATION_STATUS.DISCHARGED);

          return isAppointment || isInPatientDischarged || isInPatientActive;
        });

        setPaginatedDocs((prev) => {
          const combined = initialFetch ? newDocs : [...prev, ...newDocs];
          const uniqueMap = new Map();
          combined.forEach((doc) => uniqueMap.set(doc.id, doc));
          return Array.from(uniqueMap.values());
        });

        const newLastDoc =
          querySnapshot.docs[querySnapshot.docs.length - 1] ?? null;
        setLastDoc(newLastDoc);

        if (querySnapshot.docs.length < PAGE_SIZE) setHasMoreDocs(false);
        return newDocs;
      } catch (error) {
        console.error("Error fetching paginated conversations:", error);
        return [];
      } finally {
        setIsLoadingMore(false);
      }
    },
    [
      isLoadingMore,
      hasMoreDocs,
      lastDoc,
      currentUserId,
      roleType,
      inPatientFilter?.selectedHospitalId,
      inPatientFilter?.selectedRoomId,
    ]
  );

  const fetchGlobalDoctorUnreadChatCounts = useCallback(async () => {
    const calculateCountsForType = async (type: CONVERSATION_TYPE) => {
      const conversationRef = collection(
        firebaseService.db,
        FIREBASE_COLLECTION
      );
      const q = query(
        conversationRef,
        where("users.staff_members", "array-contains", currentUserId ?? ""),
        where("type", "==", type),
        where("isChatActive", "==", true)
      );

      const querySnapshot = await getDocs(q);

      const allRelevantChannels = querySnapshot.docs.map((doc) => doc.data());

      if (allRelevantChannels.length === 0) {
        return { mentionCount: 0, unreadCount: 0 };
      }

      const totalMentionCount = allRelevantChannels.reduce((count, chat) => {
        const lastMsgInternal = chat?.lastMessage?.internal;
        const lastMsgInternalSeen = lastMsgInternal?.seen;
        const mentions =
          lastMsgInternalSeen &&
          lastMsgInternalSeen[currentUserId]?.unReadMessageCount > 0
            ? (lastMsgInternalSeen &&
                lastMsgInternalSeen[currentUserId]?.mentionCount) ||
              0
            : 0;
        return count + mentions;
      }, 0);

      if (totalMentionCount > 0) {
        return { mentionCount: totalMentionCount, unreadCount: 0 };
      }

      const totalUnreadCount = allRelevantChannels.reduce((count, chat) => {
        const lastMsgInternal = chat?.lastMessage?.internal;
        const lastMsgInternalSeen = lastMsgInternal?.seen;
        const lastMsgPatient = chat?.lastMessage?.patient;
        const lastMsgPatientSeen = lastMsgPatient?.seen;

        const internalUnread =
          lastMsgInternal?.sender_id !== currentUserId &&
          lastMsgInternalSeen &&
          lastMsgInternalSeen[currentUserId]?.unReadMessageCount > 0
            ? 1
            : 0;
        const patientUnread =
          lastMsgPatient?.sender_id !== currentUserId &&
          lastMsgPatientSeen &&
          lastMsgPatientSeen[currentUserId]?.unReadMessageCount > 0
            ? 1
            : 0;
        const conversationUnread =
          internalUnread > 0 ? internalUnread : patientUnread;
        return count + conversationUnread;
      }, 0);

      return { mentionCount: 0, unreadCount: totalUnreadCount };
    };

    const appointmentCounts = await calculateCountsForType(
      CONVERSATION_TYPE.APPOINTMENT
    );

    const inPatientCounts = await calculateCountsForType(
      CONVERSATION_TYPE.IN_PATIENT
    );

    setGlobalUnreadCounts({
      appointment: appointmentCounts,
      inPatient: inPatientCounts,
    });
  }, [currentUserId, roleType]);

  useEffect(() => {
    if (currentUserId && doctorOrJrDoctor) {
      fetchGlobalDoctorUnreadChatCounts();
    }
  }, [currentUserId, fetchGlobalDoctorUnreadChatCounts]);

  useEffect(() => {
    if (!currentUserId || roleType === IRoleType.PATIENT) return;
    const conversationQuery = query(
      collection(firebaseService.db, FIREBASE_COLLECTION),
      where("users.staff_members", "array-contains", currentUserId)
    );

    const unsubscribe = onSnapshot(conversationQuery, (snapshot) => {
      if (isInitialListenerAttach.current) {
        isInitialListenerAttach.current = false;
        return;
      }
      if (snapshot.docChanges().length === 0) {
        return;
      }

      snapshot.docChanges().forEach(async (change: any) => {
        if (change.type === "modified") {
          const modifiedDocData = {
            ...change.doc.data(),
            id: change.doc.id,
          } as Channel;

          const updatedConversation: any = {
            ...modifiedDocData,
            id: change.doc.data()?.id,
            uniqueId: change.doc.id,
          };

          const currentConversationType =
            switchChatTab === ITabIndex.APPOINTMENTS
              ? CONVERSATION_TYPE.APPOINTMENT
              : CONVERSATION_TYPE.IN_PATIENT;
          if (
            (switchChatTab === ITabIndex.APPOINTMENTS &&
              updatedConversation.type === CONVERSATION_TYPE.APPOINTMENT) ||
            (switchChatTab === ITabIndex.IN_PATIENTS &&
              updatedConversation.type === CONVERSATION_TYPE.IN_PATIENT)
          ) {
            setPaginatedDocs((prevDocs: any) => {
              const filteredDocs = prevDocs
                .filter((doc) => doc.id !== change.doc.data()?.id)
                .filter((doc: any) => doc.type === currentConversationType);
              return [updatedConversation, ...filteredDocs];
            });
          }
        }
      });

      fetchGlobalDoctorUnreadChatCounts();
    });

    return () => {
      unsubscribe();
      isInitialListenerAttach.current = true;
    };
  }, [
    currentUserId,
    roleType,
    fetchPaginatedConversations,
    switchChatTab,
    fetchGlobalDoctorUnreadChatCounts,
    inPatientFilter?.selectedRoomId,
    inPatientFilter?.selectedHospitalId,
  ]);
  // Set list type based on role
  useEffect(() => {
    if (doctorOrJrDoctor) {
      setListType(CHAT_LIST_TYPE.INTERNAL);
    } else {
      setListType(CHAT_LIST_TYPE.PATIENT);
    }
  }, [roleType]);

  useEffect(() => {
    if (!supportChatList?.length) return;

    const unsubscribes: Unsubscribe[] = [];
    let updatedChannels = [...supportChatList];
    supportChatList.forEach((channel, index) => {
      const mainRef = collection(
        firebaseService.db,
        FIREBASE_COLLECTION,
        channel.uniqueId,
        IChatSubCollection.PATIENT
      );
      const mainQuery = query(
        mainRef.withConverter(messageConverter),
        orderBy("timestamp", "desc")
      );

      const unsubscribe = onSnapshot(mainQuery, (snapshot) => {
        if (!snapshot.empty) {
          const unReadMessages = snapshot.docs.filter(
            (doc) =>
              doc.data()?.sender_id !== currentUserId && !doc.data()?.isSeen
          );

          const updatedChannel = {
            ...updatedChannels[index],
            lastMessage: {
              patient: {
                ...snapshot.docs[0].data(),
                unReadMessageCount: unReadMessages.length,
              },
            },
          };
          updatedChannels[index] = updatedChannel;
          setSupportChat(updatedChannels[index]);
        }
      });

      unsubscribes.push(unsubscribe);
    });

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [supportChatList, currentUserId]);

  useEffect(() => {
    if (!channelList?.length) return;
    if (doctorOrJrDoctor) return;
    const unsubscribes = subscribeToChannelMessages();
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [channelList, listType, currentUserId]);

  // Effect to update channelId when videoCallChat changes
  useEffect(() => {
    if (videoCallChat && videoCallChat.length > 0) {
      const chat = videoCallChat[0];
      if (chat.uniqueId) {
        setChannelId(chat.uniqueId);
      }
    }
  }, [videoCallChat]);

  return {
    listType,
    messages,
    patientChatMediaList,
    channelList,
    enrichedChannelList,
    channelId,
    supportChat,
    handleList,
    handleChannel,
    handleSendMessage,
    markMessageAsRead,
    markAllMessagesAsRead,
    removeMemberFromChat,
    updateChatStatusOrFavorite,
    handleDocumentId,
    paginatedDocs,
    isLoadingMore,
    hasMoreDocs,
    fetchPaginatedConversations,
    setSwitchChatTab,
    fetchConversationById,
    globalUnreadCounts,
    isLoadingMessages,
  };
};

export default useFirebaseMessaging;
